import type {KeyboardEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {SectionCard} from '@/components/section-card'
import {TituloDimension} from './titulo-dimension'
import type {EstructuraCuestionario, EstructuraItem} from '@/hooks/bateria/use-bateria'

const OPCION_ON =
  'min-h-11 rounded-md border border-primary bg-accent px-2 py-1 text-xs font-semibold text-accent-foreground sm:min-h-0'
const OPCION_OFF =
  'min-h-11 rounded-md border border-input px-2 py-1 text-xs text-muted-foreground hover:border-primary/40 hover:text-foreground sm:min-h-0'

// Debe coincidir con el `data-digitacion-fila` de cada fila: el salto de foco se resuelve en el documento.
const FILA = 'data-digitacion-fila'

function moverFoco(desde: HTMLElement, direccion: 1 | -1): void {
  const filas = [...document.querySelectorAll<HTMLElement>(`[${FILA}]`)]
  const actual = filas.indexOf(desde)
  const siguiente = filas[actual + direccion]
  if (siguiente) siguiente.focus()
}

/** Un dígito elige y avanza; las flechas navegan sin responder. */
function teclasDeFila(escala: readonly string[], onChange: (valor: string) => void) {
  return (evento: KeyboardEvent<HTMLDivElement>) => {
    if (evento.key === 'ArrowDown' || evento.key === 'ArrowUp') {
      evento.preventDefault()
      moverFoco(evento.currentTarget, evento.key === 'ArrowDown' ? 1 : -1)
      return
    }
    const indice = Number(evento.key) - 1
    if (Number.isNaN(indice) || indice < 0 || indice >= escala.length) return
    evento.preventDefault()
    onChange(escala[indice])
    moverFoco(evento.currentTarget, 1)
  }
}

function EstadoPunto({answered, className = ''}: {answered: boolean; className?: string}) {
  return (
    <span
      aria-hidden="true"
      className={`size-1.5 shrink-0 rounded-full ${answered ? 'bg-primary' : 'bg-border ring-1 ring-input'} ${className}`}
    />
  )
}

function OpcionesEscala({
  itemId,
  escala,
  value,
  onChange,
  ariaLabel,
}: {
  itemId: string
  escala: readonly string[]
  value: string | null
  onChange: (value: string) => void
  ariaLabel?: string
}) {
  const {t} = useTranslation()
  return (
    <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={ariaLabel}>
      {escala.map((option, i) => (
        <button
          key={option}
          type="button"
          role="radio"
          tabIndex={-1}
          aria-checked={value === option}
          data-testid={`digitacion-opcion-${itemId}-${option}`}
          onClick={() => onChange(option)}
          className={value === option ? OPCION_ON : OPCION_OFF}>
          <span className="mr-1 font-mono text-[10px] text-muted-foreground/70">{i + 1}</span>
          {t(`admin.bateria.escala.${option}`)}
        </button>
      ))}
    </div>
  )
}

/** Solo el número del ítem: el admin lee el papel. La fila es la parada de Tab, no cada botón
 * (~685 pulsaciones en una forma B contra 125). */
function ItemRow({
  item,
  scale,
  value,
  onChange,
}: {
  item: EstructuraItem
  scale: readonly string[]
  value: string | null
  onChange: (value: string) => void
}) {
  return (
    <div
      className="surface-divider flex flex-wrap items-start gap-x-3 gap-y-1.5 border-b border-dashed py-1.5 outline-none last:border-0 hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-ring/50"
      data-testid={`digitacion-item-${item.item_id}`}
      data-answered={value ? 'true' : 'false'}
      data-digitacion-fila=""
      tabIndex={0}
      role="group"
      aria-label={String(item.numero)}
      onKeyDown={teclasDeFila(scale, onChange)}>
      <EstadoPunto answered={!!value} className="mt-3" />
      <span className="w-8 shrink-0 pt-1.5 font-mono text-xs tabular-nums text-muted-foreground">{item.numero}</span>
      <OpcionesEscala
        itemId={item.item_id}
        escala={scale}
        value={value}
        onChange={onChange}
        ariaLabel={String(item.numero)}
      />
    </div>
  )
}

const SI_NO = ['si', 'no'] as const

export function FiltroRow({
  itemId,
  condicional,
  value,
  onChange,
}: {
  itemId: string
  condicional: string
  value: string | null
  onChange: (value: string) => void
}) {
  const {t} = useTranslation()
  return (
    <div
      className="surface-divider flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-dashed py-2 outline-none last:border-0 focus-visible:ring-2 focus-visible:ring-ring/50"
      data-testid={`digitacion-filtro-${itemId}`}
      data-answered={value ? 'true' : 'false'}
      data-digitacion-fila=""
      tabIndex={0}
      role="group"
      aria-label={t(`admin.bateria.digitacion.filtro.${condicional}`)}
      onKeyDown={teclasDeFila(SI_NO, onChange)}>
      <EstadoPunto answered={!!value} />
      <span className="w-40 shrink-0 text-sm text-foreground">
        {t(`admin.bateria.digitacion.filtro.${condicional}`)}
      </span>
      <OpcionesEscala itemId={itemId} escala={SI_NO} value={value} onChange={onChange} />
    </div>
  )
}

export function CuestionarioBlock({
  cuestionario,
  respuestas,
  onChange,
}: {
  cuestionario: EstructuraCuestionario
  respuestas: Record<string, string | null>
  onChange: (itemId: string, value: string) => void
}) {
  const {t} = useTranslation()
  const scale =
    cuestionario.escala === 'estres4'
      ? (['siempre', 'casi_siempre', 'a_veces', 'nunca'] as const)
      : (['siempre', 'casi_siempre', 'algunas_veces', 'casi_nunca', 'nunca'] as const)
  const total = cuestionario.grupos.reduce((sum, g) => sum + g.items.length, 0)
  const hechos = cuestionario.grupos.reduce((sum, g) => sum + g.items.filter((i) => respuestas[i.item_id]).length, 0)
  return (
    <SectionCard
      testId={`digitacion-cuestionario-${cuestionario.cuestionario}`}
      title={t(`admin.bateria.cuestionario.${cuestionario.cuestionario}`)}
      actions={
        <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
          {t('admin.bateria.digitacion.bloqueProgreso', {hechos, total})}
        </span>
      }
      bodyClassName="p-0">
      {cuestionario.grupos.map((grupo, gi) => (
        <div key={grupo.dimension ?? gi} className="surface-divider border-b border-dashed px-5 py-3.5 last:border-0">
          {grupo.dimension && <TituloDimension dominio={grupo.dominio} dimension={grupo.dimension} />}
          <div>
            {grupo.items.map((item) => (
              <ItemRow
                key={item.item_id}
                item={item}
                scale={scale}
                value={respuestas[item.item_id] ?? null}
                onChange={(value) => onChange(item.item_id, value)}
              />
            ))}
          </div>
        </div>
      ))}
    </SectionCard>
  )
}
